// =============================================================================
// incidentsSync.js — Incident Report read/write glue (Supabase source of truth).
//
// Sibling of ingredientWastageSync.js and deliveryOrdersSync.js. It borrows:
//   * from ingredient-wastage — Supabase is the SOURCE OF TRUTH here, so the row
//     write result is SURFACED to the caller (ok / error), never swallowed. A
//     failed incident save must be visible to the staffer (the recurring
//     unread-error-body P0 lesson) — this is an EHO-inspectable safety record.
//   * from delivery-orders — the append-only shape: photos upload FIRST (so a
//     row can only ever claim photos that landed), a client token dedupes a
//     double-tap, and the write goes through a definer DOOR RPC (record_incident)
//     with a raw-insert fallback so the client is safe to deploy before the RPC
//     lands (the PGRST202/PGRST204 stale-cache trap).
//
// Photos are best-effort (a failed upload never blocks filing the report — per
// JJ the photo is recommended, not mandatory); the ROW write is not.
//
// Inert unless dualRunEnabled(): on staging / a preview build / localhost it
// returns {skipped:true} and does nothing, so those hosts are read-only mock-ups
// for visual sign-off (force a real end-to-end with ?dualrun=on).
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled, siteIdForKey, ensurePass } from "./stockSync.js";
import { incidentPhotoKey, incidentError } from "./incidentRows.js";
export { incidentError }; // re-exported so the tile (which imports this namespace) can validate before submit

const BUCKET = "delivery-photos"; // the shared private bucket; incident keys sit under <site>/incidents/…
const DOOR = "record_incident";

const londonToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });

function base64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// Kiosk wifi drops for a second or two; give anything that fails to REACH
// Supabase exactly one more go. Safe only because both halves are keyed — an
// upload re-uses the same object key and the row dedupes on idem_key — so a
// retry of a call that actually landed writes nothing.
const RETRY_AFTER_MS = 1200;
async function withOneRetry(label, fn) {
  try {
    const r = await fn();
    if (!r || !r.error) return r;
    await new Promise((res) => setTimeout(res, RETRY_AFTER_MS));
    return await fn();
  } catch (e) {
    await new Promise((res) => setTimeout(res, RETRY_AFTER_MS));
    try { return await fn(); } catch (e2) { return { error: e2 }; }
  }
}

// "The function isn't deployed here yet" — fall back to a raw insert.
const doorNotThere = (e) =>
  e?.code === "PGRST202" || /could not find the function/i.test(String(e?.message || ""));

// Best-effort upload of one photo. Never throws. upsert:false so a resend of the
// same incident is a no-op on a key that already exists. Returns the key ONLY if
// the bytes actually landed, so the row can never claim a photo that isn't there.
async function uploadPhoto(key, base64, mime) {
  const { error } = await withOneRetry("photo upload", () =>
    supabase.storage.from(BUCKET)
      .upload(key, base64ToBytes(base64), { contentType: mime || "image/jpeg", upsert: false }));
  if (error && !/exists|duplicate/i.test(error.message || "")) {
    console.warn("[incidents] photo upload failed:", error.message || error);
    return null;
  }
  return key; // already-exists counts as landed
}

// One call per filed incident, from inside the tile's requestPin callback.
//
// `photos` is an array of { dataUrl | base64, mime } captured on the tablet.
// `clientToken` is the tile's per-submit token (crypto.randomUUID()) — the thing
// that makes the append-only row appear exactly once. `staffOnDuty` is the list
// of typed witness names (the reporter is stamped server-side, not here).
//
// The raw PIN is used only to mint the pass and never reaches a row or a path.
// Returns { ok:true, id?|duplicate? } / { ok:false, error } / { ok:true, skipped }.
export async function recordIncident({
  siteKey, staffPin, type, severity, description,
  customerName = "", customerContact = "", customerNote = "",
  staffOnDuty = [], photos = [], clientToken, occurredAt = null,
  day = londonToday(), pass = null,
} = {}) {
  try {
    if (!dualRunEnabled()) return { ok: true, skipped: true };
    if (!clientToken) return { ok: false, error: "no client token" }; // refuse an unkeyed append-only row
    // Belt-and-braces: the tile already gates Submit on this, but never write a
    // malformed safety record even if a caller skips it.
    if (incidentError({ type, severity, description })) return { ok: false, error: "invalid" };

    const verified = pass || (await ensurePass(staffPin, siteKey));
    if (!verified?.ok) return { ok: false, error: "pin" };
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return { ok: false, error: "no site id for " + siteKey };

    // Photos first, sequentially (one request at a time on shopping-centre wifi),
    // collecting only the keys that actually landed.
    const paths = [];
    for (let i = 0; i < photos.length; i++) {
      const p = photos[i] || {};
      const b64 = String(p.dataUrl || p.base64 || "").split(",").pop() || "";
      if (!b64) continue;
      const key = incidentPhotoKey({ siteKey, day, token: clientToken, idx: i, mime: p.mime });
      const landed = await uploadPhoto(key, b64, p.mime);
      if (landed) paths.push(landed);
    }

    // The door first — the whole row handed over as one jsonb arg, so a stale
    // PostgREST schema cache can't refuse it column-by-column (the 14–19 Aug
    // courier-write outage). The RPC re-resolves the site, filters photo_paths to
    // this site's incidents/ prefix, dedupes on idem_key, and stamps the reporter.
    const payload = {
      site: siteKey, type, severity, description,
      customerName, customerContact, customerNote,
      staffOnDuty: Array.isArray(staffOnDuty) ? staffOnDuty : [],
      photoPaths: paths, idemKey: clientToken,
    };
    if (occurredAt) payload.occurredAt = occurredAt;

    // ONE call. "function not found" (PGRST202) is deterministic — it can never
    // clear on a 1.2s retry — so don't spend a retry on it; fall straight through
    // to the raw insert below. Anything else is the door's real answer (data or a
    // genuine refusal) and is surfaced. Retries are reserved for the transient
    // surfaces (photo upload, raw insert), where the keyed writes make them safe.
    let viaDoor;
    try { viaDoor = await supabase.rpc(DOOR, { p: payload }); } catch (e) { viaDoor = { error: e }; }
    if (!doorNotThere(viaDoor && viaDoor.error)) {
      if (viaDoor.error) return { ok: false, error: viaDoor.error };
      const d = viaDoor.data;
      if (d && d.ok === false) return { ok: false, error: d.error || "refused" };
      return { ok: true, id: d && d.id, duplicate: !!(d && d.duplicate), photos: paths.length, pass: verified };
    }

    // No door on this project yet — insert the row directly (RLS gates it on the
    // same app_session pass). return=minimal (no .select()) — a till never reads
    // an incident back, so no read policy is needed and no PII returns.
    console.warn("[incidents] no record_incident door here yet (migration 0640) — writing to the table directly");
    const rawRow = {
      site_id: siteId, type, severity, description,
      customer_name: customerName || null,
      customer_contact: customerContact || null,
      customer_note: customerNote || null,
      staff_on_duty: Array.isArray(staffOnDuty) ? staffOnDuty : [],
      photo_paths: paths, idem_key: clientToken,
    };
    if (occurredAt) rawRow.occurred_at = occurredAt;
    const { error } = await withOneRetry("incident row", () => supabase.from("incidents").insert(rawRow));
    if (error) {
      if (error.code === "23505") return { ok: true, duplicate: true, photos: paths.length, pass: verified }; // idem_key unique — already landed
      return { ok: false, error };
    }
    return { ok: true, photos: paths.length, pass: verified };
  } catch (e) {
    return { ok: false, error: e };
  }
}
